import {useAppContext} from "../../../hooks/useAppContext";
import {identity, returnUrl} from "../../../utils/helpers";
import {FormProvider, useForm} from "react-hook-form";
import {Alert, Button, Col, Form, Row} from "react-bootstrap";
import {BsCheck2Circle} from "react-icons/bs";
import {webService} from "../../../api/web";
import {TextArea} from "../../../components/textArea";
import {htmlTagsRegex} from "../../../utils/regularExpressions";
import * as React from "react";
import tokenService from "../../../utils/jwtTokenHandler";
import {useProductDetailsQuery} from "../../../hooks/useProductDetailsQuery";

const loginHref = `/account/login?returnUrl=${returnUrl}`;

export const AskQuestionForm = () => {
    const {productId} = useAppContext();
    const {model: {primaryProductId}} = useProductDetailsQuery();
    const {isAnonymous} = identity();
    const form = useForm<{ question: string }>({
        reValidateMode: 'onSubmit'
    });

    const {handleSubmit, formState: {isSubmitting, isSubmitSuccessful}} = form;

    if (isAnonymous) return <AnonymousForm/>;

    if (isSubmitSuccessful) {
        return (
            <Row>
                <Col md={{span: 8, offset: 2}} className="text-success">
                    <Alert variant="success">
                        <div className="fw-bold fs-4"><BsCheck2Circle className="me-1"/>Your question has been
                            submitted.
                        </div>
                        <p className="fs-5">
                            We will notify you once it has been answered, usually within one business day or less.
                        </p>
                    </Alert>
                </Col>
            </Row>
        );
    }

    return (
        <Row>
            <Col md={{span: 8, offset: 2}}>
                <FormProvider {...form}>
                    <Form className="fs-5"
                          onSubmit={handleSubmit(async ({question}) => {
                              try {
                                  await webService.qa.ask(question, primaryProductId ?? productId);
                              } catch {
                              }
                          })}>
                        <Form.Group controlId="askAQuestion">
                            <TextArea
                                fieldName='question'
                                displayName='Question'
                                placeholder='Type your question here...'
                                rows={5}
                                minLength={10}
                                maxLength={1000}
                                required={true}
                                className="mb-2 ask-question-input"
                                disabled={isSubmitting}
                                validation={{
                                    enabled: true,
                                    rules: {
                                        checkForHtmlTags: (value: string) => {
                                            // If the value is empty, return true
                                            if (value.length < 1) return true;

                                            // If the value is not empty and contains HTML tags, return error message
                                            if (htmlTagsRegex.test(value)) return 'HTML tags are not allowed in this field.';

                                            // If the value is not empty and does not contain HTML tags, return true to pass validation
                                            return true;
                                        }
                                    }
                                }}
                            />
                        </Form.Group>
                        <Button variant='success'
                                className='mt-2 btn-lg w-100'
                                type='submit'
                                disabled={isSubmitting}>
                            Submit Question
                        </Button>
                    </Form>
                </FormProvider>
            </Col>
        </Row>
    );
};

const AnonymousForm = () => (
    <div className="text-center">
        <a href={loginHref}
           tabIndex={0}
           role="link"
           aria-label="Go to Log in Page."
           className="text-decoration-none"
           onClick={(e) => {
               e.preventDefault();

               // Remove the token from local storage
               // before redirecting to login page
               // to prevent the user
               // from using their previous token after the redirect from a successful login
               tokenService.remove();

               // Redirect to login page
               window.location.href = loginHref;
           }}>
            <Button variant="outline-info">
                Login to ask a question
            </Button>
        </a>
    </div>
);